// src/pages/Employees/Empleado.tsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { es } from "date-fns/locale";

registerLocale("es", es);

const PageWrapper = styled.div`
  padding: 2rem;
  min-height: calc(100vh - 120px);
  background: #f9fafb;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(23, 31, 114, 0.08);
  max-width: 1200px;
  margin: 0 auto 2rem auto;
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const CalendarWrapper = styled.div`
  flex: 1 1 400px;
  display: flex;
  justify-content: center;
`;

const FormWrapper = styled.div`
  flex: 1 1 400px;
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  margin: 0.5rem 0;
`;

const Input = styled.input`
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid #ccc;
  width: 100%;
`;

const Button = styled.button`
  background: #2e186a;
  color: white;
  border: none;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  margin-top: 0.5rem;

  &:hover {
    background: #4b2ebf;
  }
`;

const LinkButton = styled.a`
  display: inline-block;
  text-decoration: none;
  text-align: center;
  background: #2e186a;
  color: white;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-weight: 700;
  margin: 0.5rem 0.5rem 0 0;

  &:hover {
    background: #4b2ebf;
  }
`;

const ButtonGroup = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Message = styled.p`
  margin-top: 1rem;
`;

const TableWrapper = styled.div`
  margin-top: 2rem;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 0.5rem;
  text-align: left;
  background: #2e186a;
  color: white;
`;

const Td = styled.td`
  padding: 0.5rem;
  border-bottom: 1px solid #ccc;
`;

const Empleado: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [horaInicio, setHoraInicio] = useState("");
  const [incidencia, setIncidencia] = useState("");
  const [message, setMessage] = useState("");
  const [registros, setRegistros] = useState<
    Array<{ fecha: string; hora_inicio: string; hora_fin: string; incidencia: string }>
  >([]);
  const [showRegistros, setShowRegistros] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [nominaLink, setNominaLink] = useState<string | null>(null);


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch("https://kevhel.com/api/checklogin.php", {
          credentials: "include",
        });
        const data = await res.json();
        if (data.logged_in) {
          setUserId(data.user_id);
        }
        else {
          alert("No estás logeado");
        }
      } catch (err) {
        alert("Error al comprobar login");
      }
    };
    fetchUser();
  }, []);

  // Dentro del componente Empleado, justo después del useEffect que obtiene userId:

  useEffect(() => {
    const fetchNominaLink = async () => {
      if (!userId) return; // Esperamos a tener userId
      try {
        const res = await fetch(`https://kevhel.com/api/get_link.php?user_id=${userId}`, {
          method: "GET",
          credentials: "include",
        });
        const data = await res.json();
        if (data.success && data.nomina_link) {
          setNominaLink(data.nomina_link);
        } else {
          console.warn("No se pudo obtener el link de nómina:", data.message);
          setNominaLink(null);
        }
      } catch (err) {
        console.error("Error al obtener el link de nómina:", err);
        setNominaLink(null);
      }
    };

    fetchNominaLink();
  }, [userId]);


  const formatLocalDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const handleSubmit = async () => {
    if (!selectedDate || !horaInicio) return;
    setMessage("");

    const fechaLocal = formatLocalDate(selectedDate);

    try {
      const res = await fetch("https://kevhel.com/api/registro_horas.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accion: "registrar_hora",
          fecha: fechaLocal,
          hora_inicio: horaInicio,
          incidencia,
          debug: true,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setMessage(`Registro guardado. Hora fin: ${data.hora_fin}`);
        setHoraInicio("");
        setIncidencia("");
      } else {
        setMessage(`Error: ${data.message}`);
      }
    } catch (err) {
      setMessage("Error en la conexion.");
    }
  };

  const fetchRegistros = async () => {
    if (!userId) return alert("Usuario no definido");
    try {
      const res = await fetch(`https://kevhel.com/api/get_registros.php?user_id=${userId}`, {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        setRegistros(data.registros);
        setShowRegistros(true);
      } else {
        alert("Error: " + data.message);
      }
    } catch (err) {
      alert("Error en la conexion.");
    }
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  return (
    <PageWrapper>
      <Card>
        <CalendarWrapper>
          <div>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date as Date)}
              dateFormat="dd/MM/yyyy"
              inline
              locale="es"
              calendarStartDay={1}
            />
          </div>
        </CalendarWrapper>

        <FormWrapper>
          <Title>Registro Horario</Title>
          {selectedDate ? (
            <>
              <FormGroup>
                <label>Hora inicio:</label>
                <Input
                  type="time"
                  value={horaInicio}
                  onChange={(e) => setHoraInicio(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <label>Incidencia (opcional):</label>
                <Input
                  type="text"
                  value={incidencia}
                  onChange={(e) => setIncidencia(e.target.value)}
                />
              </FormGroup>
              <Button onClick={handleSubmit}>Enviar</Button>
            </>
          ) : (
            <p>Selecciona un día para registrar tu horario.</p>
          )}
          {message && <Message>{message}</Message>}
        </FormWrapper>
      </Card>

      <ButtonGroup>
        <Button onClick={fetchRegistros}>Ver mis registros</Button>
        {nominaLink && (
          <LinkButton
            href={nominaLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver mis documentos
          </LinkButton>
        )}

      </ButtonGroup>

      {showRegistros && (
        <>
          {registros.length > 0 ? (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Fecha</Th>
                    <Th>Hora inicio</Th>
                    <Th>Hora fin</Th>
                    <Th>Incidencia</Th>
                  </tr>
                </thead>
                <tbody>
                  {registros.map((r, i) => (
                    <tr key={i}>
                      <Td>{formatDate(r.fecha)}</Td>
                      <Td>{r.hora_inicio}</Td>
                      <Td>{r.hora_fin}</Td>
                      <Td>{r.incidencia || "-"}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          ) : (
            <Message>No tienes registros todavía.</Message>
          )}
        </>
      )}

    </PageWrapper>
  );
};

export default Empleado;